import React, { createContext, useContext, useState, useEffect } from 'react';
import studentsService from '../../services/studentsService';
import { useAuth } from '../auth/AuthContext';

const StudentContext = createContext();

export const useStudents = () => {
  const context = useContext(StudentContext);
  if (!context) {
    throw new Error('useStudents must be used within a StudentProvider');
  }
  return context;
};

export const StudentProvider = ({ children }) => {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load students from localStorage as fallback
  const loadStudentsFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem('classtraks_students');
      if (stored) {
        const parsedStudents = JSON.parse(stored);
        if (Array.isArray(parsedStudents) && parsedStudents.length > 0) {
          console.log('StudentProvider - Loaded students from localStorage:', parsedStudents);
          return parsedStudents;
        }
      }
    } catch (error) {
      console.error('Error loading students from localStorage:', error);
    }
    return [];
  };

  // Default sample data for development
  const getDefaultStudents = () => [
    {
      id: 1,
      studentFirstName: 'Emma',
      studentLastName: 'Johnson',
      studentAge: 12,
      experience: 'Beginner',
      parentName: 'Sarah Johnson',
      parentEmail: 'sarah.johnson@email.com',
      parentPhone: '(555) 123-4567',
      enrolledClasses: ['Beginning Sewing', 'Embroidery Fundamentals'],
      dateAdded: '2025-06-15',
      studentPhoto: null,
      notes: 'Emma is a very enthusiastic student who loves learning new crafts. She has shown great interest in sewing and textile arts.',
      emergencyContact: 'Mark Johnson (555) 123-4568',
      medicalNotes: 'No known allergies',
      interests: ['Sewing', 'Fashion Design', 'Art', 'Crafts'],
      enrollmentHistory: [
        { class: 'Beginning Sewing', enrolledDate: '2025-06-15', status: 'active', progress: 75 },
        { class: 'Embroidery Fundamentals', enrolledDate: '2025-06-20', status: 'active', progress: 60 },
        { class: 'Hand Stitching Basics', enrolledDate: '2025-05-10', status: 'completed', progress: 100 }
      ]
    },
    {
      id: 2,
      studentFirstName: 'Michael',
      studentLastName: 'Chen',
      studentAge: 15,
      experience: 'Intermediate',
      parentName: 'David Chen',
      parentEmail: 'david.chen@email.com',
      parentPhone: '(555) 234-5678',
      enrolledClasses: ['Pattern Making'],
      dateAdded: '2025-06-10',
      studentPhoto: null,
      notes: 'Michael has prior sewing experience with hand stitching and is progressing well in pattern making.',
      emergencyContact: 'Lisa Chen (555) 234-5679',
      medicalNotes: '',
      interests: ['Pattern Design', 'Tailoring', 'Fashion', 'Technical Drawing'],
      enrollmentHistory: [
        { class: 'Pattern Making', enrolledDate: '2025-06-10', status: 'active', progress: 85 },
        { class: 'Basic Garment Construction', enrolledDate: '2025-05-15', status: 'completed', progress: 100 }
      ]
    },
    {
      id: 3,
      studentFirstName: 'Sofia',
      studentLastName: 'Rodriguez',
      studentAge: 14,
      experience: 'Advanced',
      parentName: 'Maria Rodriguez',
      parentEmail: 'maria.rodriguez@email.com',
      parentPhone: '(555) 345-6789',
      enrolledClasses: ['Beginning Sewing', 'Quilting Basics'],
      dateAdded: '2025-06-20',
      studentPhoto: null,
      notes: 'Sofia is an advanced student who picks up concepts quickly. She helps other students and shows leadership qualities.',
      emergencyContact: 'Carlos Rodriguez (555) 345-6790',
      medicalNotes: 'Mild food allergies',
      interests: ['Quilting', 'Textile Arts', 'Teaching', 'Fashion History'],
      enrollmentHistory: [
        { class: 'Beginning Sewing', enrolledDate: '2025-06-20', status: 'active', progress: 90 },
        { class: 'Quilting Basics', enrolledDate: '2025-06-22', status: 'active', progress: 80 },
        { class: 'Advanced Embroidery', enrolledDate: '2025-05-01', status: 'completed', progress: 100 },
        { class: 'Appliqué Techniques', enrolledDate: '2025-04-15', status: 'completed', progress: 100 }
      ]
    },
    {
      id: 4,
      studentFirstName: 'Alex',
      studentLastName: 'Thompson',
      studentAge: 10,
      experience: 'None',
      parentName: 'Jennifer Thompson',
      parentEmail: 'jen.thompson@email.com',
      parentPhone: '(555) 456-7890',
      enrolledClasses: [],
      dateAdded: '2025-06-25',
      studentPhoto: null,
      notes: 'Alex is new to sewing but shows great curiosity and eagerness to learn.',
      emergencyContact: 'Robert Thompson (555) 456-7891',
      medicalNotes: '',
      interests: ['Crafts', 'Art', 'Problem Solving', 'Design'],
      enrollmentHistory: []
    }
  ];

  // Fetch students from database
  const fetchStudents = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('StudentProvider - Fetching students from database...');
      // Request a high limit to get all students (default is 50, max is 5000)
      const response = await studentsService.getStudents({ limit: 5000 });
      console.log('StudentProvider - Database response:', response);

      // Always use database as source of truth
      if (response.students) {
        console.log('StudentProvider - Using database students:', response.students.length);
        setStudents(response.students);
        return response.students;
      }

      // If response format is unexpected, return empty array
      console.log('StudentProvider - No students in response, returning empty array');
      setStudents([]);
      return [];

    } catch (error) {
      console.error('StudentProvider - Error fetching from database:', error);
      setError(error.message);

      // On error, show empty array instead of stale localStorage data
      console.log('StudentProvider - Database error, showing empty array');
      setStudents([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Load initial data and refetch when user changes
  useEffect(() => {
    if (user) {
      // Clear existing students and refetch for new user
      setStudents([]);
      fetchStudents();
    }
  }, [user?.id]); // Refetch when user ID changes
    
  // Add new student to database
  const addStudent = async (studentData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('StudentProvider - Adding student to database:', studentData);
      
      // Try to save to database
      try {
        const newStudent = await studentsService.createStudent(studentData);
        console.log('StudentProvider - Student created in database:', newStudent);
        
        // Add to local state
        setStudents(prev => [newStudent, ...prev]);
        
        // Update localStorage
        const updatedStudents = [newStudent, ...students];
        localStorage.setItem('classtraks_students', JSON.stringify(updatedStudents));
        
        return newStudent;
      } catch (dbError) {
        console.error('StudentProvider - Database save failed, using localStorage:', dbError);
        
        // Fallback to localStorage if database fails
        const localStudent = {
          ...studentData,
          id: Math.max(...students.map(s => s.id || 0), 0) + 1,
          dateAdded: new Date().toISOString().split('T')[0],
          enrolledClasses: [],
          enrollmentHistory: []
        };
        
        // Add to state
        setStudents(prev => [localStudent, ...prev]);
        
        // Save to localStorage
        const updatedStudents = [localStudent, ...students];
        localStorage.setItem('classtraks_students', JSON.stringify(updatedStudents));
        
        return localStudent;
      }
    } catch (error) {
      console.error('StudentProvider - Error creating student:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update existing student
  const updateStudent = async (id, updates) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('StudentProvider - Updating student:', id, updates);
      
      // Try database first
      try {
        const updatedStudent = await studentsService.updateStudent(id, updates);
        console.log('StudentProvider - Student updated in database:', updatedStudent);
        
        // Update local state
        setStudents(prev => prev.map(student => 
          student.id === id ? updatedStudent : student
        ));
        
        // Update localStorage
        const updatedStudents = students.map(student => 
          student.id === id ? updatedStudent : student
        );
        localStorage.setItem('classtraks_students', JSON.stringify(updatedStudents));
        
        return updatedStudent;
      } catch (dbError) {
        console.error('StudentProvider - Database update failed, using localStorage:', dbError);
        
        // Fallback to localStorage
        const updatedStudent = {
          ...students.find(s => s.id === id || s.id === parseInt(id)),
          ...updates,
          id: id
        };
        
        // Update state
        setStudents(prev => prev.map(student => 
          (student.id === id || student.id === parseInt(id)) ? updatedStudent : student
        ));
        
        // Save to localStorage
        const updatedStudents = students.map(student => 
          (student.id === id || student.id === parseInt(id)) ? updatedStudent : student
        );
        localStorage.setItem('classtraks_students', JSON.stringify(updatedStudents));
        
        return updatedStudent;
      }
    } catch (error) {
      console.error('StudentProvider - Error updating student:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete student
  const deleteStudent = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('StudentProvider - Deleting student:', id);
      
      // Try database first
      try {
        await studentsService.deleteStudent(id);
        console.log('StudentProvider - Student deleted from database');
      } catch (dbError) {
        console.error('StudentProvider - Database delete failed:', dbError);
        // Continue with local deletion even if database fails
      }
      
      // Remove from local state (works for both DB and localStorage students)
      setStudents(prev => prev.filter(student => 
        student.id !== id && student.id !== parseInt(id)
      ));
      
      // Update localStorage
      const updatedStudents = students.filter(student => 
        student.id !== id && student.id !== parseInt(id)
      );
      localStorage.setItem('classtraks_students', JSON.stringify(updatedStudents));
      
      return true;
    } catch (error) {
      console.error('StudentProvider - Error deleting student:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete multiple students
  const deleteStudents = async (ids) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('StudentProvider - Deleting multiple students:', ids);
      
      // Try database deletions
      for (const id of ids) {
        try {
          await studentsService.deleteStudent(id);
        } catch (dbError) {
          console.error(`StudentProvider - Failed to delete student ${id} from database:`, dbError);
          // Continue with other deletions
        }
      }
      
      // Remove from local state
      setStudents(prev => prev.filter(student => 
        !ids.includes(student.id) && !ids.includes(parseInt(student.id))
      ));
      
      // Update localStorage
      const updatedStudents = students.filter(student => 
        !ids.includes(student.id) && !ids.includes(parseInt(student.id))
      );
      localStorage.setItem('classtraks_students', JSON.stringify(updatedStudents));
      
      return true;
    } catch (error) {
      console.error('StudentProvider - Error deleting students:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Clone student
  const cloneStudent = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('StudentProvider - Cloning student:', id);
      
      // Find the student to clone
      const studentToClone = students.find(s => 
        s.id === id || s.id === parseInt(id)
      );
      
      if (!studentToClone) {
        throw new Error('Student not found');
      }
      
      // Try database clone first
      try {
        const clonedStudent = await studentsService.cloneStudent(id);
        console.log('StudentProvider - Student cloned in database:', clonedStudent);
        
        // Add to local state
        setStudents(prev => [clonedStudent, ...prev]);
        
        // Update localStorage
        const updatedStudents = [clonedStudent, ...students];
        localStorage.setItem('classtraks_students', JSON.stringify(updatedStudents));
        
        return clonedStudent;
      } catch (dbError) {
        console.error('StudentProvider - Database clone failed, using localStorage:', dbError);
        
        // Fallback to localStorage clone
        const clonedStudent = {
          ...studentToClone,
          id: Math.max(...students.map(s => s.id || 0), 0) + 1,
          studentFirstName: `${studentToClone.studentFirstName} (Copy)`,
          enrolledClasses: [], // Reset enrolled classes for cloned student
          enrollmentHistory: [], // Reset enrollment history
          dateAdded: new Date().toISOString().split('T')[0]
        };
        
        // Add to state
        setStudents(prev => [clonedStudent, ...prev]);
        
        // Save to localStorage
        const updatedStudents = [clonedStudent, ...students];
        localStorage.setItem('classtraks_students', JSON.stringify(updatedStudents));
        
        return clonedStudent;
      }
    } catch (error) {
      console.error('StudentProvider - Error cloning student:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Clone multiple students
  const cloneStudents = async (ids) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('StudentProvider - Cloning multiple students:', ids);
      
      const clonedStudents = [];
      
      for (const id of ids) {
        try {
          const clonedStudent = await cloneStudent(id);
          clonedStudents.push(clonedStudent);
        } catch (error) {
          console.error(`StudentProvider - Failed to clone student ${id}:`, error);
        }
      }
      
      return clonedStudents;
    } catch (error) {
      console.error('StudentProvider - Error cloning students:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get single student by ID (handles both integer and UUID)
  const getStudent = (id) => {
    console.log('StudentProvider - getStudent called with ID:', id, typeof id);
    console.log('StudentProvider - Available students:', students);
    
    const student = students.find(student => {
      // Handle both string and number IDs for backwards compatibility
      const match = student.id === id || 
                   student.id === parseInt(id) || 
                   String(student.id) === String(id);
      
      if (match) {
        console.log('StudentProvider - Found matching student:', student);
      }
      
      return match;
    });
    
    console.log('StudentProvider - getStudent result:', student);
    return student;
  };

  // Enroll student in class
  const enrollStudent = async (studentId, classId) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('StudentProvider - Enrolling student in class:', studentId, classId);
      
      try {
        const result = await studentsService.enrollStudent(studentId, classId);
        console.log('StudentProvider - Student enrolled in database:', result);
        
        // Update local state - would need class name from response
        // This is a simplified version - real implementation would update enrolledClasses
        return result;
      } catch (dbError) {
        console.error('StudentProvider - Database enrollment failed:', dbError);
        throw dbError;
      }
    } catch (error) {
      console.error('StudentProvider - Error enrolling student:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Unenroll student from class
  const unenrollStudent = async (studentId, classId) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('StudentProvider - Unenrolling student from class:', studentId, classId);
      
      try {
        const result = await studentsService.unenrollStudent(studentId, classId);
        console.log('StudentProvider - Student unenrolled from database:', result);
        
        // Update local state - would need class name from response
        // This is a simplified version - real implementation would update enrolledClasses
        return result;
      } catch (dbError) {
        console.error('StudentProvider - Database unenrollment failed:', dbError);
        throw dbError;
      }
    } catch (error) {
      console.error('StudentProvider - Error unenrolling student:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Refresh students from database
  const refreshStudents = () => {
    return fetchStudents();
  };

  const value = {
    students,
    loading,
    error,
    fetchStudents,
    addStudent,
    updateStudent,
    deleteStudent,
    deleteStudents,
    cloneStudent,
    cloneStudents,
    getStudent,
    enrollStudent,
    unenrollStudent,
    refreshStudents
  };

  return (
    <StudentContext.Provider value={value}>
      {children}
    </StudentContext.Provider>
  );
};